import useTitanApp from "@/hooks/useTitanApp";
import {
  addPatientDocuments,
  renamePatientDocument,
  getPatientDocuments,
  emailPatientDocument,
  deletePatientDocument,
  getPatientDocumentById,
} from "@/dispatchers/patientDispatchers";
import { IntercomTrack } from "@/data/IntercomTrack";
import { intercomActionPatientDocumentText } from "@/data/constantStrings";
export interface PatientDocument {
  id: number;
  title: string;
  type:
    | "GP Letter"
    | "Lab Results"
    | "Prescriptions"
    | "Consent Letter"
    | "Intervention"
    | "General"
    | "General Letter"
    | "General Email";
  date: string;
  description: string;
  status: "Available" | "Pending" | "Expired";
  size?: string;
  downloadUrl?: string;
  fileContent?: string;
}

export interface BackendPatientDocument {
  patientId: string;
  title: string;
  description: string;
  size: string;
  status: number; // 1 = Available
  fileContent: string; // base64 file string
  contentType: string;
  consultationId?: string;
  type: any;
}

export const documentTypes: PatientDocument["type"][] = [
  "GP Letter",
  "Lab Results",
  "Prescriptions",
  "Consent Letter",
  "Intervention",
  "General",
  "General Letter",
  "General Email",
];

export const documentStatusOptions = [
  { value: "Available", label: "Available", color: "green" },
  { value: "Pending", label: "Pending", color: "orange" },
  { value: "Expired", label: "Expired", color: "red" },
] as const;

export interface DocumentData {
  base64Content: string;
  fileExtension: string;
  success: boolean;
  title?: string;
  description?: string;
}

export enum DocumentTypeEnum {
  GPLetter = 1,
  LabResult = 2,
  Prescription = 3,
  ConsentLetter = 4,
  Intervention = 5,
  General = 6,
  Certificate = 7,
  GeneralLetter = 8,
  GeneralEmail = 9,
}

export function getDocumentTypeEnum(type: string): DocumentTypeEnum {
  const normalized = type.trim().toLowerCase();

  switch (normalized) {
    case "gp letter":
      return DocumentTypeEnum.GPLetter;
    case "lab result":
    case "lab results":
      return DocumentTypeEnum.LabResult;
    case "prescription":
    case "prescriptions":
      return DocumentTypeEnum.Prescription;
    case "consent letter":
      return DocumentTypeEnum.ConsentLetter;
    case "intervention":
      return DocumentTypeEnum.Intervention;
    case "general":
      return DocumentTypeEnum.General;
    case "general letter":
      return DocumentTypeEnum.GeneralLetter;
    case "general email":
      return DocumentTypeEnum.GeneralEmail;
    case "certificate":
      return DocumentTypeEnum.Certificate;
    default:
      throw new Error(`Unknown document type: ${type}`);
  }
}

export const usePatientDocumentsState = () => {
  const { patientState, appState } = useTitanApp();
  const { currentPharmacy, currentUser, company, currentPharmacyId } =
    appState || {};
  const {
    patientDocuments: backendDocuments,
    patientDocumentsLoader,
    selectedPatientDetails,
    summaryDocumentId,
  } = patientState || {};
  const { patientID } = selectedPatientDetails || {};
  // 🔄 Map backend docs to frontend docs before returning
  const mappedDocuments: PatientDocument[] = Array.isArray(backendDocuments)
    ? backendDocuments.map(mapBackendToFrontendDoc)
    : [];

  return {
    mappedDocuments,
    patientDocumentsLoader,
    patientID,
    currentPharmacy,
    currentPharmacyId,
    summaryDocumentId,
  };
};

export const fileToBase64 = (
  file: File,
): Promise<{ base64File: string; contentType: string }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file); // result -> "data:application/pdf;base64,...."

    reader.onload = () => {
      const result = reader.result as string;

      // Split into "metadata" and "base64"
      const [metadata, base64Part] = result.split(",");
      const contentTypeMatch = metadata.match(/:(.*?);/);

      const contentType = contentTypeMatch
        ? contentTypeMatch[1]
        : "application/octet-stream"; // fallback
      const base64File = base64Part.replace(/\s/g, ""); // clean spaces/newlines
      resolve({ base64File, contentType });
    };

    reader.onerror = (error) => reject(error);
  });
};

export const addDocument = async (document: BackendPatientDocument) => {
  try {
    await addPatientDocuments(document);
    await getPatientDocuments(document?.patientId);

    return document;
  } catch (error) {
    console.error("Failed to add patient document:", error);
    throw error;
  }
};

export const addDocumentFromActions = async (
  document: BackendPatientDocument,
) => {
  try {
    const apiResponse = await addPatientDocuments(document);
    await getPatientDocuments(document.patientId);

    return apiResponse;
  } catch (error) {
    console.error("Failed to add patient document:", error);
    throw error;
  }
};

export const mapBackendToFrontendDoc = (doc: any): PatientDocument => {
  const typeMap: Record<number, PatientDocument["type"]> = {
    1: "GP Letter",
    2: "Lab Results",
    3: "Prescriptions",
    4: "Consent Letter",
    5: "Intervention",
    6: "General",
    8: "General Letter",
    9: "General Email",
  };

  const statusMap: Record<number, PatientDocument["status"]> = {
    1: "Available",
    2: "Expired", // your backend "Archived" → map to "Expired" here
  };

  return {
    id: doc._id, // frontend-only, unique key
    title: doc.title,
    type: typeMap[doc.type] ?? "General",
    date: new Date(doc.date).toLocaleDateString("en-GB"),
    description: doc.description,
    status: statusMap[doc.status] ?? "Available",
    size: doc.size,
    downloadUrl: doc.downloadUrl || undefined,
  };
};

export const renameDocument = async (
  documentId: string,
  newTitle: string,
  patientId?: string,
) => {
  try {
    await renamePatientDocument({
      documentId,
      title: newTitle,
    });
    if (patientId) {
      getPatientDocuments(patientId);
    }

    return true;
  } catch (error) {
    console.error("Failed to rename patient document:", error);
    throw error;
  }
};

export const emailDocuments = async (
  documentIds: string[],
  recipientEmail: string,
  message: string,
  pharmacyId: string,
) => {
  try {
    await emailPatientDocument({
      email: recipientEmail,
      message,
      documentIds,
      pharmacyId,
    });

    return true;
  } catch (error) {
    console.error("Failed to send patient documents email:", error);
    throw error;
  }
};

export const removeDocument = async (
  documentId: string,
  patientId?: string,
) => {
  try {
    const result = await deletePatientDocument(documentId);

    if (result && patientId) {
      // Refresh docs after delete
      await getPatientDocuments(patientId);
    }

    return !!result; // true if success, false if not
  } catch (error) {
    console.error("Failed to delete patient document:", error);
    return false;
  }
};

export const fetchDocumentById = async (documentId: string) => {
  try {
    const result = await getPatientDocumentById(documentId);

    if (result?.base64Content) {
      return {
        base64Content: result.base64Content,
        fileExtension: result.fileExtenstion,
        success: true,
      };
    }

    if (result?.message) {
      return {
        base64Content: result.message,
        fileExtenstion: ".pdf",
        success: true,
      };
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch document by ID:", error);
    return null;
  }
};

export const handleDownloadDocument = async (documentData: any) => {
  try {
    const details = await fetchDocumentById(documentData.id.toString());

    if (!details?.base64Content || !details?.fileExtension) {
      console.error("Missing base64 content or file extension");
      return;
    }

    let mimeType = "application/octet-stream";
    switch (details.fileExtension.toLowerCase()) {
      case ".pdf":
        mimeType = "application/pdf";
        break;
      case ".png":
        mimeType = "image/png";
        break;
      case ".jpg":
      case ".jpeg":
        mimeType = "image/jpeg";
        break;
      case ".txt":
        mimeType = "text/plain";
        break;
    }

    const byteCharacters = atob(details.base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const fileName =
      (details.title || documentData.title || "document") +
      details.fileExtension;
    a.download = fileName.replace(/[^a-z0-9_.-]/gi, "_");

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to download document:", error);
  }
};
